/**
 * ADC Channel Visualization Component
 * Shows the 5 ADC channels with conductivity values (white to red)
 * Gray carbon lanes connect the channels
 * Shows bridge pattern based on diaper model code
 */

import React from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath, Circle, Bar } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { ClipDataPoint, LiquidAddition } from '../../lib/api-clip-monitor';
import { DiaperModel } from '../../lib/types';
import { format } from 'date-fns';
import { useClipDataStore } from '../../stores/clipDataStore';

const LANE_LABELS = ['1', '2', '4', '8', '16', 'Ref'];
const CHANNEL_LABELS = ['C1', 'C2', 'C3', 'C4', 'C5'];
const CHANNEL_WEIGHTS = [1, 2, 4, 8, 16];
const CHANNEL_COLORS = ['#ef4444', '#f97316', '#3b82f6', '#8b5cf6', '#22c55e'];

const LIQUID_EXPECTED_COLOR = '#60a5fa';
const LIQUID_PATTERN_FILL = '#bfdbfe';
const LIQUID_DASH_ARRAY = '4,4';

interface ChannelHistoryPoint {
  timestamp: Date;
  value: number;
  delta?: number; // Change from previous measurement
}

interface ChannelChartProps {
  data: ChannelHistoryPoint[];
  color: string;
  threshold?: number | null;
  baseline?: number | null;
  timelineRange: [Date, Date] | null;
  channelIndex: number;
  useDynamicRange: boolean;
  hoveredTimestamp?: string | null;
  onHoverMeasurement?: (timestamp: string) => void;
  onLeaveHover?: () => void;
  liquidAdditions?: LiquidAddition[];
  channelLabel: string; // 'C1', 'C2', etc.
}

interface ChannelChartInnerProps extends ChannelChartProps {
  width: number;
  height: number;
}

const ChannelChartInner: React.FC<ChannelChartInnerProps> = ({
  data,
  color,
  threshold,
  baseline,
  timelineRange,
  channelIndex,
  useDynamicRange,
  hoveredTimestamp,
  onHoverMeasurement,
  onLeaveHover,
  liquidAdditions = [],
  channelLabel,
  width,
  height,
}) => {
  const { showTooltip, hideTooltip, tooltipData, tooltipLeft = 0, tooltipTop = 0 } = useTooltip<ChannelHistoryPoint>();
  const [localHover, setLocalHover] = React.useState(false);

  const margin = { top: 10, right: 10, bottom: 30, left: 45 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const domainStart = timelineRange ? timelineRange[0] : data.length > 0 ? data[0].timestamp : null;
  const domainEnd = timelineRange ? timelineRange[1] : data.length > 0 ? data[data.length - 1].timestamp : null;

  const adjustedEnd = (domainStart && domainEnd && domainStart.getTime() === domainEnd.getTime())
    ? new Date(domainEnd.getTime() + 60 * 1000)
    : domainEnd;

  const xScale = domainStart && adjustedEnd
    ? scaleTime({ range: [0, innerWidth], domain: [domainStart, adjustedEnd] })
    : scaleTime({ range: [0, innerWidth], domain: [new Date(), new Date()] });
  
  // Dynamic range: Y-axis from baseline to leak threshold
  let yDomain: [number, number] = [0, 255];
  if (useDynamicRange && baseline != null && threshold != null) {
    const minValue = Math.max(0, Math.floor(baseline) - 5); // Add small padding
    const maxValue = Math.min(255, Math.ceil(threshold) + 5);
    yDomain = [minValue, maxValue];
  }
  const yScale = scaleLinear({ range: [innerHeight, 0], domain: yDomain });

  const clampedThreshold = threshold != null ? Math.max(yDomain[0], Math.min(yDomain[1], threshold)) : null;
  const clampedBaseline = baseline != null ? Math.max(yDomain[0], Math.min(yDomain[1], baseline)) : null;

  // Find external hover point
  const externalHoverPoint = React.useMemo(() => {
    if (localHover || !hoveredTimestamp || data.length === 0) return null;
    return data.find((d) => d.timestamp.toISOString() === hoveredTimestamp) || null;
  }, [localHover, hoveredTimestamp, data]);

  // Show external hover tooltip
  React.useEffect(() => {
    if (externalHoverPoint) {
      let yPos = yScale(externalHoverPoint.value);
      yPos = Math.max(0, Math.min(innerHeight, yPos));
      
      showTooltip({
        tooltipData: externalHoverPoint,
        tooltipLeft: xScale(externalHoverPoint.timestamp) + margin.left,
        tooltipTop: yPos + margin.top,
      });
    } else if (!localHover) {
      hideTooltip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalHoverPoint, localHover]);

  if (!domainStart || !domainEnd || innerWidth <= 0 || innerHeight <= 0) {
    return (
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <rect width={innerWidth} height={innerHeight} rx={4} fill="#f9fafb" />
          <text x={innerWidth / 2} y={innerHeight / 2} textAnchor="middle" fill="#9ca3af" fontSize={10}>
            No data
          </text>
        </Group>
      </svg>
    );
  }

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    setLocalHover(true);
    const point = localPoint(event);
    if (!point) return;
    
    const x = point.x - margin.left;
    const timestamp = xScale.invert(x);
    
    // Find closest data point
    let closest: ChannelHistoryPoint | null = null;
    let minDistance = Infinity;
    
    data.forEach((d: ChannelHistoryPoint) => {
      const distance = Math.abs(d.timestamp.getTime() - timestamp.getTime());
      if (distance < minDistance) {
        minDistance = distance;
        closest = d;
      }
    });
    
    if (closest) {
      const closestPoint: ChannelHistoryPoint = closest;
      let yPos = yScale(closestPoint.value);
      yPos = Math.max(0, Math.min(innerHeight, yPos));
      
      showTooltip({
        tooltipData: closestPoint,
        tooltipLeft: xScale(closestPoint.timestamp) + margin.left,
        tooltipTop: yPos + margin.top,
      });
      const closestIso = closestPoint.timestamp.toISOString();
      if (onHoverMeasurement && closestIso !== hoveredTimestamp) {
        onHoverMeasurement(closestIso);
      }
    }
  };

  return (
    <div style={{ position: 'relative', width, height }}>
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <rect width={innerWidth} height={innerHeight} rx={4} fill="#f9fafb" />
          
          {/* Baseline vertical line */}
          {clampedBaseline != null && (
            <line
              x1={0}
              x2={innerWidth}
              y1={yScale(clampedBaseline)}
              y2={yScale(clampedBaseline)}
              stroke="#22c55e"
              strokeWidth={1.5}
              strokeDasharray="4,4"
              opacity={0.7}
            />
          )}

          {/* Leak threshold line */}
          {clampedThreshold != null && (
            <line
              x1={0}
              x2={innerWidth}
              y1={yScale(clampedThreshold)}
              y2={yScale(clampedThreshold)}
              stroke="#ef4444"
              strokeWidth={1.5}
              strokeDasharray="4,4"
              opacity={0.7}
            />
          )}

          {data.length > 0 ? (
            <>
              <LinePath
                data={data}
                x={(d) => xScale(d.timestamp)}
                y={(d) => yScale(d.value)}
                stroke={color}
                strokeWidth={2}
                curve={curveMonotoneX}
              />
              
              {/* Pattern definition for liquid addition areas */}
              <defs>
                <pattern id={`liquidPattern-${channelIndex}`} width="8" height="8" patternUnits="userSpaceOnUse">
                  <rect width="8" height="8" fill={LIQUID_PATTERN_FILL} opacity="0.3" />
                  <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" stroke={LIQUID_EXPECTED_COLOR} strokeWidth="1" opacity="0.5" />
                </pattern>
              </defs>

              {/* Filled rectangular areas for liquid additions to this channel */}
              {liquidAdditions && liquidAdditions
                .filter((addition) => addition.location === channelLabel)
                .map((addition, idx) => {
                  const startTime = new Date(addition.timestamp);
                  const endTime = new Date(startTime.getTime() + addition.duration * 1000);
                  
                  // Only render if within the visible time range
                  if (timelineRange) {
                    if (endTime < timelineRange[0] || startTime > timelineRange[1]) {
                      return null;
                    }
                  }
                  
                  const x1 = xScale(startTime);
                  const x2 = xScale(endTime);
                  const width = x2 - x1;
                  
                  // Calculate flow rate (mL per second) - this represents the "power" of the addition
                  const flowRate = addition.volume / addition.duration;
                  
                  // Calculate relative flow strength (normalized to typical range)
                  // Scale: 0-1 where 20 mL/s = 1.0 (typical normal pour)
                  const relativeFlowStrength = Math.min(flowRate / 20, 1.5); // Allow up to 1.5x for very fast pours
                  
                  // Height varies from 15% to 35% of chart height based on flow rate
                  const heightPercent = 0.15 + (relativeFlowStrength * 0.20);
                  const barHeight = innerHeight * heightPercent;
                  
                  // Position from top
                  const y = 0;
                  
                  // Check if this addition is hovered
                  const isHovered = hoveredTimestamp === addition.timestamp;
                  
                  return (
                    <React.Fragment key={`liquid-${idx}`}>
                      {/* Red vertical lines at start and end when hovered */}
                      {isHovered && (
                        <>
                          <line
                            x1={x1}
                            y1={0}
                            x2={x1}
                            y2={innerHeight}
                            stroke="#ef4444"
                            strokeWidth={3}
                            opacity={0.8}
                          />
                          <line
                            x1={x2}
                            y1={0}
                            x2={x2}
                            y2={innerHeight}
                            stroke="#ef4444"
                            strokeWidth={3}
                            opacity={0.8}
                          />
                        </>
                      )}
                      
                      {/* Striped filled area */}
                      <Bar
                        x={x1}
                        y={y}
                        width={width}
                        height={barHeight}
                        fill={`url(#liquidPattern-${channelIndex})`}
                        opacity={isHovered ? 0.3 : 0.9}
                      />
                      {/* Dashed border outline - RED when hovered */}
                      <rect
                        x={x1}
                        y={y}
                        width={width}
                        height={barHeight}
                        fill="none"
                        stroke={isHovered ? '#ef4444' : LIQUID_EXPECTED_COLOR}
                        strokeWidth={isHovered ? 4 : 2}
                        strokeDasharray={LIQUID_DASH_ARRAY}
                        opacity={1}
                      />
                      {/* Invisible hover target (larger hit area) with smart detection */}
                      <rect
                        x={x1}
                        y={y}
                        width={width}
                        height={barHeight}
                        fill="transparent"
                        style={{ cursor: 'pointer', pointerEvents: 'all' }}
                        onMouseEnter={(event) => {
                          if (!onHoverMeasurement) return;
                          
                          // Smart detection: check if mouse is close to any data point
                          const point = localPoint(event) || { x: 0, y: 0 };
                          const mouseX = point.x;
                          const mouseY = point.y;
                          
                          // Check if within 15px of any ADC data point
                          const nearMeasurement = data.some(d => {
                            const dotX = xScale(d.timestamp);
                            const dotY = yScale(d.value);
                            const dist = Math.sqrt(Math.pow(mouseX - dotX, 2) + Math.pow(mouseY - dotY, 2));
                            return dist < 15;
                          });
                          
                          // Only trigger liquid hover if NOT near a measurement
                          if (!nearMeasurement) {
                            onHoverMeasurement(addition.timestamp);
                          }
                        }}
                        onMouseLeave={() => {
                          if (onLeaveHover) {
                            onLeaveHover();
                          }
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              
              {data.map((d, i) => {
                const cx = xScale(d.timestamp);
                const cy = yScale(d.value);
                const hasDelta = d.delta !== undefined && d.delta !== 0;
                const iso = d.timestamp.toISOString();
                const isHovered = hoveredTimestamp === iso;
                
                return (
                  <React.Fragment key={i}>
                    <Circle
                      cx={cx}
                      cy={cy}
                      r={isHovered ? 5 : 3}
                      fill={color}
                      stroke={isHovered ? '#1D4ED8' : '#ffffff'}
                      strokeWidth={isHovered ? 2 : 1}
                    />
                    {/* Delta text label */}
                    {hasDelta && (
                      <text
                        x={cx}
                        y={cy - 10}
                        textAnchor="middle"
                        fill={d.delta! > 0 ? '#3b82f6' : '#ef4444'}
                        fontSize={9}
                        fontWeight="bold"
                      >
                        {d.delta! > 0 ? '+' : ''}{d.delta}
                      </text>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            <text x={innerWidth / 2} y={innerHeight / 2} textAnchor="middle" fill="#9ca3af" fontSize={10}>
              No data
            </text>
          )}

          {/* Vertical line for hovered measurement */}
          {hoveredTimestamp && data.length > 0 && (() => {
            const match = data.find((d) => d.timestamp.toISOString() === hoveredTimestamp);
            if (match) {
              return (
                <line
                  x1={xScale(match.timestamp)}
                  x2={xScale(match.timestamp)}
                  y1={0}
                  y2={innerHeight}
                  stroke="#6366f1"
                  strokeWidth={2}
                  strokeDasharray="6,4"
                  opacity={0.6}
                />
              );
            }
            return null;
          })()}

          {/* Y-axis */}
          <AxisLeft
            scale={yScale}
            numTicks={5}
            tickFormat={(v) => String(v)}
            stroke="#e5e7eb"
            tickStroke="#e5e7eb"
            tickLabelProps={() => ({
              fill: '#6b7280',
              fontSize: 10,
              textAnchor: 'end',
              dx: -4,
              dy: 3,
            })}
          />

          {/* X-axis */}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={4}
            tickFormat={(v) => format(v as Date, 'HH:mm:ss')}
            stroke="#e5e7eb"
            tickStroke="#e5e7eb"
            tickLabelProps={() => ({
              fill: '#6b7280',
              fontSize: 10,
              textAnchor: 'middle',
              dy: 4,
            })}
          />

          {/* Hover area */}
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
              setLocalHover(false);
              hideTooltip();
              onLeaveHover?.();
            }}
            style={{ pointerEvents: 'all' }}
          />
        </Group>
      </svg>

      {/* Tooltip for ADC measurements */}
      {tooltipData && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <div>
            <strong>{format(tooltipData.timestamp, 'HH:mm:ss')}</strong>
          </div>
          <div>Ch{channelIndex + 1}: {tooltipData.value}</div>
          {tooltipData.delta !== undefined && tooltipData.delta !== 0 && (
            <div style={{ color: tooltipData.delta > 0 ? '#3b82f6' : '#ef4444' }}>
              {tooltipData.delta > 0 ? '+' : ''}{tooltipData.delta}
            </div>
          )}
        </TooltipWithBounds>
      )}
      
      {/* Tooltip for liquid additions */}
      {hoveredTimestamp && liquidAdditions && liquidAdditions.length > 0 && (() => {
        const hoveredAddition = liquidAdditions.find(add => add.timestamp === hoveredTimestamp && add.location === channelLabel);
        if (!hoveredAddition) return null;
        
        const flowRate = hoveredAddition.volume / hoveredAddition.duration;
        const startTime = new Date(hoveredAddition.timestamp);
        const x = xScale(startTime);
        
        return (
          <TooltipWithBounds 
            key="liquid-tooltip"
            top={20}
            left={x}
            style={{
              position: 'absolute',
              backgroundColor: 'rgba(59, 130, 246, 0.95)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 1001,
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '2px', color: '#bfdbfe' }}>
              💧 Liquid @ {channelLabel}
            </div>
            <div>{format(startTime, 'HH:mm:ss')}</div>
            <div>{hoveredAddition.volume} mL / {hoveredAddition.duration}s</div>
            <div>{flowRate.toFixed(1)} mL/s</div>
          </TooltipWithBounds>
        );
      })()}
    </div>
  );
};

// Wrapper component with ParentSize
const ChannelChart: React.FC<ChannelChartProps> = (props) => {
  return (
    <ParentSize>
      {({ width, height }) => (
        <ChannelChartInner {...props} width={width} height={height} />
      )}
    </ParentSize>
  );
};

  interface ADCVisualizationProps {
    latestData: ClipDataPoint | null;
    historyData: ClipDataPoint[];
    leakAdcThresholds?: number[] | null;
    diaperModel?: DiaperModel | null;
    baselineAdcValues?: number[] | null;
    useDynamicRange: boolean;
    liquidAdditions?: LiquidAddition[];
    brushSelection?: { startTime: Date | null; endTime: Date | null };
  }

export const ADCVisualization: React.FC<ADCVisualizationProps> = ({
  latestData,
  historyData,
  leakAdcThresholds,
  diaperModel,
  baselineAdcValues,
  useDynamicRange,
  liquidAdditions = [],
  brushSelection,
}) => {
  const hoveredMeasurementTimestamp = useClipDataStore((state) => state.hoveredMeasurementTimestamp);
  const hoveredLiquidTimestamp = useClipDataStore((state) => state.hoveredLiquidAdditionTimestamp);
  const setHoveredMeasurement = useClipDataStore((state) => state.setHoveredMeasurement);
  const setHoveredLiquidAddition = useClipDataStore((state) => state.setHoveredLiquidAddition);

  // Combined hover state - show measurement hover OR liquid hover
  const hoveredTimestamp = hoveredMeasurementTimestamp || hoveredLiquidTimestamp;

  const handleHover = React.useCallback((timestamp: string) => {
    // Check if this timestamp is a liquid addition
    const isLiquid = liquidAdditions.some(add => add.timestamp === timestamp);
    if (isLiquid) {
      setHoveredLiquidAddition(timestamp);
      setHoveredMeasurement(null);
    } else {
      setHoveredMeasurement(timestamp);
      setHoveredLiquidAddition(null);
    }
  }, [setHoveredMeasurement, setHoveredLiquidAddition, liquidAdditions]);

  const handleHoverEnd = React.useCallback(() => {
    setHoveredMeasurement(null);
    setHoveredLiquidAddition(null);
  }, [setHoveredMeasurement, setHoveredLiquidAddition]);

  // Filter history data based on brush selection
  const filteredHistoryData = React.useMemo(() => {
    if (!brushSelection || (brushSelection.startTime === null && brushSelection.endTime === null)) {
      return historyData; // No brush selection, show all
    }

    const startTime = brushSelection.startTime || new Date(historyData[0]?.timestamp);
    const endTime = brushSelection.endTime || new Date(historyData[historyData.length - 1]?.timestamp);

    return historyData.filter((record) => {
      const recordTime = new Date(record.timestamp);
      return recordTime >= startTime && recordTime <= endTime;
    });
  }, [historyData, brushSelection]);

  const timelineRange = React.useMemo<[Date, Date] | null>(() => {
    if (filteredHistoryData.length === 0) return null;
    const start = new Date(filteredHistoryData[0].timestamp);
    const end = new Date(filteredHistoryData[filteredHistoryData.length - 1].timestamp);
    return [start, end];
  }, [filteredHistoryData]);

  const channelHistories = React.useMemo<ChannelHistoryPoint[][]>(() => {
    return CHANNEL_LABELS.map((_, channelIndex) => {
      const points = filteredHistoryData
        .map((record) => {
          const value = record.parsed.adcValues?.[channelIndex];
          if (typeof value !== 'number') return null;
          return {
            timestamp: new Date(record.timestamp),
            value,
          };
        })
        .filter((point): point is ChannelHistoryPoint => point !== null);
      
      // Calculate deltas
      return points.map((point, i) => {
        if (i === 0) {
          return { ...point, delta: 0 };
        }
        const delta = point.value - points[i - 1].value;
        return { ...point, delta };
      });
    });
  }, [filteredHistoryData]);

  const mostRecent = latestData ?? (filteredHistoryData.length > 0 ? filteredHistoryData[filteredHistoryData.length - 1] : null);

  if (!mostRecent) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-600">
        <p className="text-2xl mb-2">📉</p>
        <p>No ADC data available in the selected time range.</p>
      </div>
    );
  }

  const adcValuesRaw = mostRecent.parsed.adcValues || [];
  const adcValues = Array.from({ length: 5 }, (_, index) => adcValuesRaw[index] ?? 0);

  const hasLeakThresholds = leakAdcThresholds && leakAdcThresholds.some((v) => (v ?? 0) > 0);

  const getChannelColor = (value: number, channelIndex: number): string => {
    // If we have baseline and leak thresholds, use dynamic range (blue gradient)
    if (baselineAdcValues && leakAdcThresholds && leakAdcThresholds[channelIndex]) {
      const baseline = baselineAdcValues[channelIndex];
      const leakThreshold = leakAdcThresholds[channelIndex];
      const delta = value - baseline;
      const maxDelta = leakThreshold - baseline;
      
      // Calculate percentage: 0% (at baseline) to 100% (at leak threshold)
      const percentage = maxDelta > 0 ? Math.max(0, Math.min(100, (delta / maxDelta) * 100)) : 0;
      
      // Blue gradient: white (0%) to bright blue (100%)
      const intensity = Math.floor((percentage / 100) * 255);
      const red = 255 - intensity;
      const green = 255 - intensity;
      const blue = 255;
      return `rgb(${red}, ${green}, ${blue})`;
    }
    
    // Fallback: absolute value coloring (red gradient)
    const intensity = Math.min(255, Math.max(0, value));
    const red = 255;
    const green = 255 - intensity;
    const blue = 255 - intensity;
    return `rgb(${red}, ${green}, ${blue})`;
  };

  const modelCode = mostRecent.parsed.diaperModel || diaperModel?.code || 0;
  const bridgedChannels = new Set<number>();

  if (modelCode > 0) {
    CHANNEL_WEIGHTS.forEach((weight, index) => {
      if ((modelCode & weight) === weight) {
        bridgedChannels.add(index);
      }
    });
  }

  const isChannelBridged = (index: number) => bridgedChannels.has(index);

  const laneRowHeight = 12; // Reduced from 52
  const channelRowHeight = 160; // Increased to accommodate axes
  const bridgeColumnWidth = 60; // Reduced for thin line
  const totalHeight = LANE_LABELS.length * laneRowHeight + CHANNEL_LABELS.length * channelRowHeight;

  const laneCenterY = (index: number) => index * (laneRowHeight + channelRowHeight) + laneRowHeight / 2;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">Conductivity Channels (Carbon Sensors)</h3>
        {hasLeakThresholds && (
          <span className="text-xs text-red-600 font-semibold">
            ⚠️ Leak thresholds active
          </span>
        )}
      </div>

      <div className="text-xs text-gray-600 mb-3 text-center">
        <strong>Orientation:</strong> LEFT = BACK (ID bridges) • RIGHT = FRONT (clip connector)
        {modelCode > 0 && (
          <span className="ml-2">
            • <strong>Model Code:</strong> {modelCode} = {Array.from(bridgedChannels).map((i) => `${CHANNEL_LABELS[i]}(${CHANNEL_WEIGHTS[i]})`).join('+')}
          </span>
        )}
      </div>

      <div className="relative" style={{ minHeight: `${totalHeight}px` }}>
        {modelCode > 0 && (
          <div
            className="absolute left-12 top-0"
            style={{ width: `${bridgeColumnWidth}px`, height: `${totalHeight}px`, pointerEvents: 'none' }}
          >
            <div className="absolute -top-6 left-0 text-xs font-semibold text-gray-600">
              ID Zone
            </div>
            <svg width={bridgeColumnWidth} height={totalHeight}>
              {CHANNEL_LABELS.map((channelLabel, channelIndex) => {
                if (!isChannelBridged(channelIndex)) return null;
                const topY = laneCenterY(channelIndex);
                const bottomY = laneCenterY(channelIndex + 1);

                return (
                  <g key={`bridge-${channelIndex}`}>
                    {/* Thin straight line */}
                    <line
                      x1={bridgeColumnWidth / 2}
                      y1={topY}
                      x2={bridgeColumnWidth / 2}
                      y2={bottomY}
                      stroke="#9ca3af"
                      strokeWidth={2}
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        )}

        <div className="space-y-1">
          {LANE_LABELS.map((laneLabel, laneIndex) => (
            <React.Fragment key={`lane-${laneIndex}`}>
              <div className="flex items-center gap-2" style={{ height: `${laneRowHeight}px` }}>
                <div className="w-12 text-center">
                  <div className="inline-block w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gray-800">
                    {laneLabel}
                  </div>
                </div>
                <div className="flex-1 bg-gray-400 h-2 rounded"></div>
              </div>

              {laneIndex < CHANNEL_LABELS.length && (
                <div className="flex items-start gap-2" style={{ height: `${channelRowHeight}px` }}>
                  {/* Channel label in square on the left */}
                  <div className="w-12 flex items-center justify-center" style={{ marginTop: `${channelRowHeight / 2 - 16}px` }}>
                    <div className="w-8 h-8 rounded border-2 border-gray-600 bg-white flex items-center justify-center text-xs font-bold text-gray-700">
                      {CHANNEL_LABELS[laneIndex]}
                    </div>
                  </div>

                  <div
                    className="relative flex-1 h-full"
                    style={{
                      marginLeft: modelCode > 0 ? `${bridgeColumnWidth - 12}px` : '0px',
                    }}
                  >
                    <div className="absolute inset-0 rounded-lg border border-gray-200 bg-white">
                      <ChannelChart
                        data={channelHistories[laneIndex] || []}
                        color={CHANNEL_COLORS[laneIndex % CHANNEL_COLORS.length]}
                        threshold={leakAdcThresholds?.[laneIndex]}
                        baseline={baselineAdcValues?.[laneIndex]}
                        timelineRange={timelineRange}
                        channelIndex={laneIndex}
                        useDynamicRange={useDynamicRange}
                        hoveredTimestamp={hoveredTimestamp}
                        onHoverMeasurement={handleHover}
                        onLeaveHover={handleHoverEnd}
                        liquidAdditions={liquidAdditions}
                        channelLabel={CHANNEL_LABELS[laneIndex]}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1 min-w-[90px]">
                    <div
                      className="w-20 h-12 rounded-lg flex items-center justify-center font-semibold text-lg border-2 border-gray-300"
                      style={{ backgroundColor: getChannelColor(adcValues[laneIndex], laneIndex) }}
                    >
                      {adcValues[laneIndex]}
                    </div>
                    {leakAdcThresholds && leakAdcThresholds[laneIndex] !== undefined && leakAdcThresholds[laneIndex] > 0 && (
                      <div className="text-xs text-red-600 font-semibold">
                        ⚠️ {leakAdcThresholds[laneIndex]}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-400 rounded"></div>
            <span className="text-gray-700 font-medium">Carbon Lane</span>
          </div>
          {baselineAdcValues && leakAdcThresholds ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white border-2 border-gray-300 rounded"></div>
                <span className="text-gray-700">Baseline (0%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: getChannelColor(128, 2) }}></div>
                <span className="text-gray-700">Mid-range</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: 'rgb(0, 0, 255)' }}></div>
                <span className="text-gray-700">Leak (100%)</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white border-2 border-gray-300 rounded"></div>
                <span className="text-gray-700">0 (Dry)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: getChannelColor(128, 0) }}></div>
                <span className="text-gray-700">128</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-500 rounded"></div>
                <span className="text-gray-700">255 (Wet)</span>
              </div>
            </>
          )}
          {baselineAdcValues && (
            <div className="flex items-center gap-2">
              <div style={{ width: '24px', height: '2px', borderTop: '2px dashed #22c55e' }}></div>
              <span className="text-gray-700">Baseline</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div style={{ width: '24px', height: '2px', borderTop: '2px dashed #ef4444' }}></div>
            <span className="text-gray-700">Leak</span>
          </div>
        </div>
      </div>
    </div>
  );
};
